export type PdfRow = Array<[string, string]>;

function escapeHtml(value: string) {
    return value
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#39;");
}

export function renderTable(title: string, rows: PdfRow) {
    if (rows.length === 0) {
        return "";
    }

    return `
        <section style="margin-top: 24px;">
            <h2 style="font-size: 16px; margin: 0 0 10px; color: #0f172a;">${escapeHtml(title)}</h2>
            <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
                <tbody>
                    ${rows.map(([label, value]) => `
                        <tr>
                            <td style="width: 32%; padding: 8px 10px; border: 1px solid #d8e1eb; color: #64748b; background: #f8fafc;">${escapeHtml(label)}</td>
                            <td style="padding: 8px 10px; border: 1px solid #d8e1eb; color: #0f172a;">${escapeHtml(value || "—")}</td>
                        </tr>
                    `).join("")}
                </tbody>
            </table>
        </section>
    `;
}

function buildPrintableHtml(title: string, subtitle: string, sections: string[], landscape = false) {
    return `
        <!doctype html>
        <html lang="bg">
            <head>
                <meta charset="utf-8" />
                <title>${escapeHtml(title)}</title>
                <style>
                    body { font-family: Arial, sans-serif; margin: 32px; color: #0f172a; }
                    h1 { margin: 0 0 8px; font-size: 26px; }
                    p { margin: 0; color: #475569; line-height: 1.55; }
                    @media print {
                        body { margin: 18px; }
                        ${landscape ? "@page { size: A4 landscape; margin: 10mm; }" : ""}
                    }
                </style>
            </head>
            <body>
                <h1>${escapeHtml(title)}</h1>
                <p>${escapeHtml(subtitle)}</p>
                ${sections.join("")}
            </body>
        </html>
    `;
}

export interface PdfPreviewHandle {
    close: () => void;
    update: (title: string, subtitle: string, sections: string[]) => void;
}

function openPrintOverlay(html: string): PdfPreviewHandle {
    document.getElementById("crm-print-overlay")?.remove();

    const overlay = document.createElement("div");
    overlay.id = "crm-print-overlay";
    overlay.style.cssText =
        "position:fixed;inset:0;z-index:1400;background:rgba(15,23,42,0.5);display:flex;align-items:center;justify-content:center;padding:16px;box-sizing:border-box;";

    const panel = document.createElement("div");
    panel.style.cssText =
        "background:#fff;border-radius:12px;width:min(960px,100%);height:min(90vh,900px);display:flex;flex-direction:column;overflow:hidden;box-shadow:0 20px 50px rgba(15,23,42,0.25);";

    const toolbar = document.createElement("div");
    toolbar.style.cssText = "display:flex;gap:8px;padding:12px;border-bottom:1px solid #e2e8f0;flex-shrink:0;";

    const printBtn = document.createElement("button");
    printBtn.type = "button";
    printBtn.textContent = "Печат / Save as PDF";
    printBtn.style.cssText =
        "border:1px solid #cbd5e1;background:#fff;color:#0f172a;border-radius:999px;padding:8px 14px;cursor:pointer;font-weight:600;";
    printBtn.onclick = () => iframe.contentWindow?.print();

    const closeBtn = document.createElement("button");
    closeBtn.type = "button";
    closeBtn.textContent = "Затвори";
    closeBtn.style.cssText =
        "border:1px solid #cbd5e1;background:#fff;color:#0f172a;border-radius:999px;padding:8px 14px;cursor:pointer;";

    const iframe = document.createElement("iframe");
    iframe.title = "Преглед за PDF";
    iframe.style.cssText = "flex:1;border:none;width:100%;";
    iframe.srcdoc = html;

    const close = () => {
        document.removeEventListener("keydown", onKeyDown);
        overlay.remove();
    };

    const onKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
            close();
        }
    };

    closeBtn.onclick = close;
    document.addEventListener("keydown", onKeyDown);

    toolbar.append(printBtn, closeBtn);
    panel.append(toolbar, iframe);
    overlay.append(panel);
    overlay.addEventListener("click", (event) => {
        if (event.target === overlay) {
            close();
        }
    });
    document.body.append(overlay);

    return {
        close,
        update(nextTitle: string, nextSubtitle: string, sections: string[]) {
            iframe.srcdoc = buildPrintableHtml(nextTitle, nextSubtitle, sections);
        },
    };
}

export function openPdfDocument(title: string, subtitle: string, sections: string[], landscape = false) {
    openPrintOverlay(buildPrintableHtml(title, subtitle, sections, landscape));
}

export function openPdfDocumentPending(title: string, subtitle: string): PdfPreviewHandle {
    const loadingSection = `
        <section style="margin-top: 28px; padding: 18px; border: 1px dashed #cbd5e1; border-radius: 12px; color: #475569;">
            Зареждане на данните...
        </section>
    `;

    return openPrintOverlay(buildPrintableHtml(title, subtitle, [loadingSection]));
}
